import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export type QuoteShippingLocation_ =
  Database['public']['Tables']['quote_shipping_location']['Row']

export type QuoteShippingLocationWithLocation = QuoteShippingLocation_ & {
  shipping_locations:
    | Database['public']['Tables']['shipping_locations']['Row']
    | null
}

export const useQuoteShippingLocationsApi = ({
  quote_id,
}: {
  quote_id?: number | null
}) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: quoteShippingLocations, isPending: isLoading } = useQuery({
    queryKey: [queryKeys.quoteShippingLocations, quote_id],
    queryFn: async () => {
      if (!quote_id) return []
      const { data, error } = await supabase
        .from('quote_shipping_location')
        .select('*, shipping_locations(*)')
        .eq('quote_id', quote_id)
        .order('created_at', { ascending: true })

      if (error) throw error
      return (data ?? []) as QuoteShippingLocationWithLocation[]
    },
    enabled: !!quote_id,
  })

  const insertQuoteShippingLocation = useMutation({
    mutationFn: async ({
      quote_id: qid,
      shipping_location_id,
    }: {
      quote_id: number
      shipping_location_id: number
    }) => {
      const { data, error } = await supabase
        .from('quote_shipping_location')
        .insert({ quote_id: qid, shipping_location_id })
        .select('id')
        .single()

      if (error) throw error
      return data.id
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteShippingLocations, variables.quote_id],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteShippingLocations],
      })
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to add destination',
        description: error.message,
      })
    },
  })

  const upsertQuoteShippingLocations = useMutation({
    mutationFn: async ({
      quote_id: qid,
      shipping_location_ids,
      notes_by_shipping_location_id = {},
    }: {
      quote_id: number
      shipping_location_ids: number[]
      /** Per shipping_locations.id — stored on quote_shipping_location.notes */
      notes_by_shipping_location_id?: Record<number, string>
    }) => {
      await supabase
        .from('quote_shipping_location')
        .delete()
        .eq('quote_id', qid)

      if (shipping_location_ids.length > 0) {
        const rows = shipping_location_ids.map((shipping_location_id) => {
          const raw =
            notes_by_shipping_location_id[shipping_location_id]?.trim()
          return {
            quote_id: qid,
            shipping_location_id,
            notes: raw ? raw : null,
          }
        })
        const { error } = await supabase
          .from('quote_shipping_location')
          .insert(rows)

        if (error) throw error
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteShippingLocations, variables.quote_id],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteShippingLocations],
      })
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to save destinations',
        description: error.message,
      })
    },
  })

  return {
    quoteShippingLocations: quoteShippingLocations ?? [],
    isLoading,
    insertQuoteShippingLocation,
    upsertQuoteShippingLocations,
  }
}
